import Rails from "@rails/ujs"
import { Controller } from "stimulus"

// 添付資料（管理画面 /admin/reservations/:id/edit_attached_image ・患者画面
// /users/reservations/:id/attached_image_edit）のアップロードを扱う。
//
// YAS-16 の方針（詳細は .steering/20260725-fix-attached-image-upload-freeze/design.md）:
//  R1: 3本の Ajax は「フォーム全体の FormData」をやめ、必要なフィールドだけを送る。
//      アップ済みファイルのバイナリが以後のリクエスト（追加・次のアップロード・確定）に載らない。
//  R2: すべての Ajax に error コールバックを付け、DOM 参照は null ガードを通す。
//  R3: 送信中は既存のメッセージ領域に「アップロード中…」を出し、file input を disabled にする。
//  R4: 「＋」は全再描画ではなく1フィールドの追記。index は JS が唯一の採番者になる。
//  R6-1: iOS WebKit の空body送信対策として、change 直後にファイルをメモリへ読み込み、
//        メモリ由来の File を送る（arrayBuffer() は非同期なので完了を待ってから送信する）。
//
// YAS-31: 同じ partial（users/reservations/_upload_image）を使う予約問診の症状写真へ横展開するため、
// モデル名などの差分を getter に切り出して基底クラスとしても使えるようにした（挙動は変えていない）。
// サブクラスは reservation_interview_controller.js を参照。
export default class extends Controller {
  static targets = ['form','addButton','addError'];
  static classes = ['errorm','hide'];
  static values = {
    uploadurl: String,
    removeimageurl: String,
    addimagefieldurl: String,
    uploadingMessage: String,
    networkErrorMessage: String
  };

  // ---- サブクラスで差し替える箇所 -----------------------------------------
  // DOM の id 規約（<modelName>_<targetName>_error_message / <modelName>_id_<index>）と
  // FormData のキー（<modelName>[image][<index>]）は両者で一致しているため、これだけで吸収できる。

  get modelName(){ return "attached_image"; }        // FormData キー・DOM id の接頭辞
  get responseIdKey(){ return "ai_id"; }             // 保存済みレコード ID を返す JSON のキー
  get uploadFieldId(){ return "attached_image_upload_field"; } // 「＋」で追記する先の要素 id

  // 「＋」でフィールドを1つ増やした直後に呼ばれる。既定は何もしない（添付資料の現行挙動）。
  afterAddField(nextIndex){}

  initialize(){
    // アップロード進行中に「確定する」を押すと通信が中断し、孤児 AttachedImage が生まれるため、
    // 飛行中は submit を無効化する（設計レビュー 論点1）。必ず 0 に戻ること。
    this.inFlight = 0;
  }

  // ---- 共通ヘルパー -------------------------------------------------------

  el(id){
    return document.getElementById(id);
  }

  messageArea(targetName){
    return this.el(this.modelName + "_" + targetName + "_error_message");
  }

  // kind: 'progress' | 'error' | 'clear'
  showMessage(targetName, text, kind){
    const area = this.messageArea(targetName);
    if(area == null){ return; }
    area.textContent = (kind === 'clear') ? "" : (text || "");
    if(kind === 'progress'){
      area.classList.remove("is-danger");
      area.classList.add("has-text-grey");
    }else {
      area.classList.remove("has-text-grey");
      area.classList.add("is-danger");
    }
  }

  submitButtons(){
    if(!this.hasFormTarget){ return []; }
    return Array.from(this.formTarget.querySelectorAll('[type="submit"]'));
  }

  beginRequest(){
    this.inFlight += 1;
    this.submitButtons().forEach((b) => { b.disabled = true; });
  }

  endRequest(){
    this.inFlight = Math.max(0, this.inFlight - 1);
    if(this.inFlight === 0){
      this.submitButtons().forEach((b) => { b.disabled = false; });
    }
  }

  networkErrorMessage(){
    return this.networkErrorMessageValue || "";
  }

  // 空body由来（サーバが 400 + code:"empty_body" を返した）ときだけ自動リトライする。
  // 通信断やタイムアウトで再送すると、実ファイル（最大20MB）の送信を繰り返して悪化するため。
  isEmptyBodyError(response, xhr){
    return xhr != null && xhr.status === 400 && response != null && response.code === "empty_body";
  }

  // ---- 「＋（追加）」 -----------------------------------------------------

  addUploadField(){
    const uploadField = this.el(this.uploadFieldId);
    const indexField = this.el("image_index");
    if(uploadField == null || indexField == null){ return; }

    // 採番は送信前に同期的に済ませる。success を待って +1 すると、連打時に
    // 同じ index のフィールドが二重に生成され、確定時に無言で紐付けが落ちる。
    const nextIndex = Number(indexField.value) + 1;
    if(!Number.isFinite(nextIndex)){ return; }
    indexField.value = nextIndex;
    // 採番と同じタイミングでフックを呼ぶ（AJAX の成否で採番済み index と表示がズレないようにする）
    this.afterAddField(nextIndex);

    const button = this.hasAddButtonTarget ? this.addButtonTarget.querySelector("button") : null;
    if(button != null){ button.disabled = true; }
    if(this.hasAddErrorTarget){ this.addErrorTarget.textContent = ""; }

    Rails.ajax({
      url: this.addimagefieldurlValue,
      type: "POST",
      dataType: 'html',
      // 文字列で渡すと form-urlencoded になり、multipart ですらなくなる（R1）
      data: new URLSearchParams({index: nextIndex}).toString(),
      success: (response) => {
        if(response == null || response.body == null){ return; }
        uploadField.insertAdjacentHTML('beforeend', response.body.innerHTML);
      },
      error: () => {
        if(this.hasAddErrorTarget){
          this.addErrorTarget.textContent = this.networkErrorMessage();
        }
      },
      complete: () => {
        if(button != null){ button.disabled = false; }
      }
    });
  }

  // ---- 自動アップロード ---------------------------------------------------

  autoUpload(event){
    const input = event.currentTarget;
    const params = event.params;
    const targetName = params.targetname;
    const index = params.index;
    const file = (input.files != null) ? input.files[0] : null;
    if(file == null || !file.name){ return; }

    input.disabled = true;
    this.beginRequest();
    this.showMessage(targetName, this.uploadingMessageValue, 'progress');

    // .catch が無いと、then 内で同期例外が出たときに input.disabled と inFlight が
    // 戻らず「確定する」が押せないまま固着する（本タスクが直そうとしている症状そのもの）。
    this.materialize(file).then((f) => {
      this.postUpload(f, input, targetName, index, 1);
    }).catch(() => {
      this.finishUpload(input, targetName, this.networkErrorMessage());
    });
  }

  // iOS WebKit ではファイルハンドル由来の File を送ると body が空になることがあるため、
  // 送信前にメモリへ読み込んだ File に差し替える。読めない場合は元の File のまま続行する。
  materialize(file){
    if(typeof file.arrayBuffer !== 'function'){ return Promise.resolve(file); }
    return file.arrayBuffer()
      .then((buf) => new File([buf], file.name, { type: file.type }))
      .catch(() => file);
  }

  postUpload(file, input, targetName, index, attempt){
    const formData = new FormData();
    formData.append(this.modelName + "[image][" + index + "]", file);
    const idField = this.el(this.modelName + "_id_" + index);
    formData.append(this.modelName + "[id][" + index + "]", (idField != null) ? idField.value : "");
    formData.append("type", targetName);
    formData.append("index", index);

    Rails.ajax({
      url: this.uploadurlValue,
      type: "POST",
      dataType: 'json',
      data: formData,
      success: (response) => {
        // JSON パースに失敗すると rails-ujs は文字列をそのまま渡してくるため、
        // オブジェクトでない応答は異常扱いにする。
        if(response == null || typeof response !== "object"){
          this.finishUpload(input, targetName, this.networkErrorMessage());
          return;
        }
        if(response.error != null){
          // バリデーションNG（サイズ超過等）。リトライしない。
          this.finishUpload(input, targetName, response.error);
          return;
        }
        // ai_id（問診は di_id）が無い応答で描画すると idField に undefined が入り、
        // 確定時に AttachedImage.find("undefined") → RecordNotFound（500）になる。
        if(response[this.responseIdKey] == null){
          this.finishUpload(input, targetName, this.networkErrorMessage());
          return;
        }
        this.renderUploaded(response, targetName, index);
        this.finishUpload(input, targetName, null);
      },
      error: (response, statusText, xhr) => {
        if(attempt < 2 && this.isEmptyBodyError(response, xhr)){
          setTimeout(() => this.postUpload(file, input, targetName, index, attempt + 1), 500);
          return;
        }
        const message = (response != null && response.error != null) ? response.error : this.networkErrorMessage();
        this.finishUpload(input, targetName, message);
      }
    });
  }

  renderUploaded(response, targetName, index){
    const viewDiv = this.el("image_view_" + targetName);
    if(viewDiv == null){ return; }
    const image = viewDiv.querySelector("img");
    const fileInfo = viewDiv.querySelector(".attached-file-info");
    const fileName = viewDiv.querySelector(".attached-file-name");
    const fileLink = viewDiv.querySelector(".attached-file-link");
    // image_file キーを返さないエンドポイント（問診の interview_auto_upload）では
    // 画像表示分岐を既定にする。undefined を falsy 判定すると PDF 表示分岐に落ちてしまう。
    const isImage = (response.image_file == null) ? true : !!response.image_file;
    if(isImage){
      if(image != null){
        image.src = response.image_url;
        image.classList.remove(this.hideClass);
      }
      if(fileInfo != null){ fileInfo.classList.add(this.hideClass); }
    }else {
      if(image != null){
        image.src = "";
        image.classList.add(this.hideClass);
      }
      if(fileName != null){ fileName.textContent = response.file_name; }
      if(fileLink != null){ fileLink.href = response.file_url; }
      if(fileInfo != null){ fileInfo.classList.remove(this.hideClass); }
    }
    viewDiv.classList.remove(this.hideClass);
    const uploadDiv = this.el("image_upload_" + targetName);
    if(uploadDiv != null){ uploadDiv.classList.add(this.hideClass); }
    const idField = this.el(this.modelName + "_id_" + index);
    if(idField != null){ idField.value = response[this.responseIdKey]; }
  }

  // 成否にかかわらず file input を復帰させ、値をクリアする（R1: 以後のリクエストにバイナリを載せない）。
  finishUpload(input, targetName, errorMessage){
    input.disabled = false;
    input.value = "";
    if(errorMessage != null && errorMessage !== ""){
      this.showMessage(targetName, errorMessage, 'error');
    }else {
      this.showMessage(targetName, "", 'clear');
    }
    this.endRequest();
  }

  // ---- 削除 ---------------------------------------------------------------

  removeImage(event){
    const params = event.params;
    const targetName = params.targetname;
    const index = params.index;
    const idField = this.el(this.modelName + "_id_" + index);
    if(idField == null){ return; }

    const formData = new FormData();
    formData.append(this.modelName + "[id][" + index + "]", idField.value);
    formData.append("type", targetName);
    formData.append("index", index);

    this.beginRequest();
    Rails.ajax({
      url: this.removeimageurlValue,
      type: "POST",
      dataType: 'json',
      data: formData,
      success: () => {
        this.renderRemoved(targetName, index);
        this.showMessage(targetName, "", 'clear');
      },
      error: (response) => {
        // 削除できていないのでプレビューは消さない
        const message = (response != null && response.error != null) ? response.error : this.networkErrorMessage();
        this.showMessage(targetName, message, 'error');
      },
      complete: () => {
        this.endRequest();
      }
    });
  }

  renderRemoved(targetName, index){
    const viewDiv = this.el("image_view_" + targetName);
    if(viewDiv != null){
      const image = viewDiv.querySelector("img");
      const fileInfo = viewDiv.querySelector(".attached-file-info");
      const fileName = viewDiv.querySelector(".attached-file-name");
      const fileLink = viewDiv.querySelector(".attached-file-link");
      if(image != null){
        image.src = "";
        image.classList.remove(this.hideClass);
      }
      if(fileInfo != null){ fileInfo.classList.add(this.hideClass); }
      if(fileName != null){ fileName.textContent = ""; }
      if(fileLink != null){ fileLink.href = "#"; }
      viewDiv.classList.add(this.hideClass);
    }
    const idField = this.el(this.modelName + "_id_" + index);
    if(idField != null){ idField.value = null; }
    const uploadDiv = this.el("image_upload_" + targetName);
    if(uploadDiv != null){ uploadDiv.classList.remove(this.hideClass); }
  }

}
